// Funnel-wizard step machine + step nav (FF-15 / MIS-998).
//
// `deriveStep` maps FF-8's computed setup-status to the FIRST incomplete wizard
// step — the wizard's resume point. It is SERVER truth: the wizard never stores
// progress locally, so `/funnels/new?domainId=X` re-derives the step on every
// load (refresh, deep link, another device — same answer).
//
// `WizardNav` renders the 7 steps with per-check states:
//   done    — the step's checks already pass (it sits before the derived
//             frontier); clickable (back-nav is free).
//   current — the step being shown; highlighted, not clickable.
//   todo    — the derived frontier itself when the user has navigated back;
//             clickable (it is exactly where the server says to resume).
//   locked  — beyond the frontier; disabled (forward navigation is gated).

import { cn } from '@/lib/utils';
import type { DomainSetupStatus } from './useSetupStatus';

/** The 7 wizard steps, in order. */
export const WIZARD_STEPS = [
  { id: 1, key: 'domain', label: 'Domain' },
  { id: 2, key: 'dns', label: 'DNS & SSL' },
  { id: 3, key: 'landers', label: 'Landers' },
  { id: 4, key: 'integrations', label: 'Integrations' },
  { id: 5, key: 'numbers', label: 'Numbers' },
  { id: 6, key: 'binding', label: 'Ad binding' },
  { id: 7, key: 'review', label: 'Review' },
] as const;

export type WizardStep = (typeof WIZARD_STEPS)[number];
export type WizardStepId = WizardStep['id'];

export const FIRST_STEP = 1 satisfies WizardStepId;
export const LAST_STEP = 7 satisfies WizardStepId;

/** Clamp an arbitrary number into [1, max] as a valid step id. */
export function clampStep(value: number, max: WizardStepId = LAST_STEP): WizardStepId {
  const n = Math.min(Math.max(Math.trunc(value), FIRST_STEP), max);
  return n as WizardStepId;
}

/**
 * Derive the resume step from the server-computed checklist: the first step
 * whose checks fail. No status (no domain selected yet) → step 1.
 *
 *   no domainId → 1; !dns||!ssl → 2; zero landers → 3;
 *   missing any of capi/trackdrive/postback creds → 4;
 *   numberRouting not ok → 5; fbBinding not ok → 6; else 7 (review).
 */
export function deriveStep(status: DomainSetupStatus | undefined): WizardStepId {
  if (!status) return 1;
  const { checks } = status;
  if (!checks.dns || !checks.ssl) return 2;
  if (checks.landers.count === 0) return 3;
  if (!(checks.capiCreds && checks.trackdriveCreds && checks.postbackToken)) return 4;
  if (!checks.numberRouting.ok) return 5;
  if (!checks.fbBinding.ok) return 6;
  return 7;
}

export type WizardStepState = 'done' | 'current' | 'todo' | 'locked';

/** Resolve one step's visual/interaction state (see file header). */
export function stepState(
  step: WizardStepId,
  currentStep: WizardStepId,
  derivedStep: WizardStepId,
): WizardStepState {
  if (step === currentStep) return 'current';
  if (step > derivedStep) return 'locked';
  if (step < derivedStep) return 'done';
  return 'todo';
}

// Dark-theme tones per state (ui/* CSS vars — matches Badge/Button palettes).
const ROW_CLASSES: Record<WizardStepState, string> = {
  done: 'text-[var(--text)] hover:bg-[var(--surface-3)]',
  current: 'bg-[var(--surface-3)] text-[var(--text)] ring-1 ring-[var(--accent)]',
  todo: 'text-[var(--muted)] hover:bg-[var(--surface-3)] hover:text-[var(--text)]',
  locked: 'text-[var(--muted-2)]',
};

const BUBBLE_CLASSES: Record<WizardStepState, string> = {
  done: 'bg-[rgba(0,212,170,0.1)] text-[var(--accent-light)]',
  current: 'bg-[linear-gradient(135deg,#2dffc4,#00d4aa)] text-[#08080d]',
  todo: 'border border-[var(--border-strong)] text-[var(--muted)]',
  locked: 'border border-[var(--border-soft)] text-[var(--muted-2)]',
};

export interface WizardNavProps {
  /** The step currently shown (already clamped to the frontier by the route). */
  currentStep: WizardStepId;
  /** The server-derived frontier — the furthest reachable step (forward gate). */
  derivedStep: WizardStepId;
  onSelectStep: (step: WizardStepId) => void;
}

/** The wizard's step list. Back-nav free; forward gated past the frontier. */
export function WizardNav({
  currentStep,
  derivedStep,
  onSelectStep,
}: WizardNavProps): React.ReactElement {
  return (
    <nav aria-label="Funnel setup steps">
      <ol className="flex flex-col gap-1">
        {WIZARD_STEPS.map((step) => {
          const state = stepState(step.id, currentStep, derivedStep);
          const locked = state === 'locked';
          return (
            <li key={step.id}>
              <button
                type="button"
                disabled={locked}
                aria-current={state === 'current' ? 'step' : undefined}
                data-state={state}
                onClick={() => {
                  if (!locked && step.id !== currentStep) onSelectStep(step.id);
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                  'disabled:cursor-not-allowed',
                  ROW_CLASSES[state],
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    BUBBLE_CLASSES[state],
                  )}
                >
                  {state === 'done' ? '✓' : step.id}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
